import { FormEvent, useEffect, useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react'
import { Checkbox, Link } from '@ui'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import * as yup from 'yup'
import { FormService } from '@services'
import { useRouter } from 'next/router'

interface IPressReleasePdfForm {
  isOpen: boolean
  onClose: () => void
  pdfUrl: string
}

type FormValues = {
  name: string
  surname: string
  email: string
  companyName: string
  kvkk: boolean
}

type FormErrors = Partial<Record<keyof FormValues, string>>

const emptyForm: FormValues = {
  name: '',
  surname: '',
  email: '',
  companyName: '',
  kvkk: false,
}

const inputStyles = {
  px: '10px',
  border: '1px solid',
  borderColor: 'black.primary3',
  variant: 'secondary' as const,
  size: 'secondary' as const,
  focusBorderColor: 'black.primary3',
  errorBorderColor: 'red.primary',
}

const triggerFileDownload = (url: string) => {
  if (!url) return

  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const PressReleasePdfForm = ({
  isOpen,
  onClose,
  pdfUrl,
}: IPressReleasePdfForm) => {
  const { t } = useTranslation()
  const { locale } = useRouter()
  const toast = useToast()
  const [form, setForm] = useState<FormValues>(emptyForm)
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm)
      setErrors({})
    }
  }, [isOpen])

  const updateField = <K extends keyof FormValues>(
    field: K,
    value: FormValues[K]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const validateForm = async (): Promise<boolean> => {
    const schema = yup.object().shape({
      name: yup.string().required(t('form:error.requiredField')),
      surname: yup.string().required(t('form:error.requiredField')),
      email: yup
        .string()
        .email(t('form:error.invalidEmail'))
        .required(t('form:error.requiredField')),
      companyName: yup.string().required(t('form:error.requiredField')),
      kvkk: yup.bool().oneOf([true], t('form:error.requiredField')),
    })

    try {
      await schema.validate(form, { abortEarly: false })
      setErrors({})
      return true
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const nextErrors: FormErrors = {}
        error.inner.forEach((item) => {
          if (item.path) {
            nextErrors[item.path as keyof FormValues] = item.message
          }
        })
        setErrors(nextErrors)
      }
      return false
    }
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()

    if (!pdfUrl) {
      toast({
        title: t('form:error.commonTitle'),
        description: t('form:error.commonDescription'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    const isValid = await validateForm()
    if (!isValid) return

    setIsSubmitting(true)
    try {
      await FormService.post({
        webform_id: 'pr_register',
        name: form.name,
        surname: form.surname,
        email: form.email,
        companyname: form.companyName,
        pdfurl: pdfUrl,
        locale,
        consent: true,
      })

      toast({
        title: t('form:success.primary'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      })
      setForm(emptyForm)
      setErrors({})
      onClose()
      setTimeout(() => {
        triggerFileDownload(pdfUrl)
      }, 400)
    } catch (error) {
      toast({
        title: t('form:error.commonTitle'),
        description: t('form:error.commonDescription'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay
        bgColor="blackAlpha.primary80"
        transition="all 0.3s ease"
        sx={{
          backdropFilter: 'blur(8px)',
        }}
      />
      <ModalContent bgColor="white.primary">
        <ModalBody py="padding.primary2">
          <Box
            as="form"
            sx={{
              '& > * + *': {
                mt: '6',
              },
            }}
            noValidate
            onSubmit={onSubmit}
          >
            <FormControl isInvalid={!!errors.name}>
              <FormLabel fontWeight="medium" fontSize="sm" mb="2">
                {t('form:field.name')}
              </FormLabel>
              <Input
                type="text"
                {...inputStyles}
                value={form.name}
                onChange={(event) => updateField('name', event.target.value)}
              />
              <FormErrorMessage color="red.primary">
                {errors.name}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.surname}>
              <FormLabel fontWeight="medium" fontSize="sm" mb="2">
                {t('form:field.surname')}
              </FormLabel>
              <Input
                type="text"
                {...inputStyles}
                value={form.surname}
                onChange={(event) => updateField('surname', event.target.value)}
              />
              <FormErrorMessage color="red.primary">
                {errors.surname}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.email}>
              <FormLabel fontWeight="medium" fontSize="sm" mb="2">
                {t('form:field.email')}
              </FormLabel>
              <Input
                type="email"
                {...inputStyles}
                value={form.email}
                onChange={(event) => updateField('email', event.target.value)}
              />
              <FormErrorMessage color="red.primary">
                {errors.email}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.companyName}>
              <FormLabel fontWeight="medium" fontSize="sm" mb="2">
                {t('form:field.companyName')}
              </FormLabel>
              <Input
                type="text"
                {...inputStyles}
                value={form.companyName}
                onChange={(event) =>
                  updateField('companyName', event.target.value)
                }
              />
              <FormErrorMessage color="red.primary">
                {errors.companyName}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.kvkk}>
              <Checkbox
                id="press-kvkk"
                isChecked={form.kvkk}
                onChange={(event) => updateField('kvkk', event.target.checked)}
              >
                <Trans
                  i18nKey="legal:press.kvkkLabel"
                  components={[
                    <Text
                      key="kvkkTextComponent"
                      fontWeight="light"
                      fontSize="sm"
                    />,
                    <Link
                      fontWeight="medium"
                      key="kvkkLinkComponent"
                      href="/kvkk"
                      target="_blank"
                    />,
                  ]}
                />
              </Checkbox>
              <FormErrorMessage color="red.primary">
                {errors.kvkk}
              </FormErrorMessage>
            </FormControl>

            <Button type="submit" isLoading={isSubmitting}>
              {t('common:download')}
            </Button>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default PressReleasePdfForm
